/**
 * Firebase Data Hooks
 * 
 * Real-time data from Firestore for the AI Controller.
 * Uses 'ai_' prefixed collections.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getSensorStatus } from '../utils/sensorStatus';
import { registrySoilEc, type RegistrySoilEc } from '../utils/registrySoilEc';
import { AlertThresholdsConfig } from '../types/alerts';
import { 
  collection, 
  doc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '../firebase';
import { sendControllerCommand } from '../utils/commands';
import type {
  ActiveRecipeKey,
  ActiveTargetStage,
  BatchScheduleAutomationConfig,
  BatchScheduleMode,
} from '../../shared/types/BatchSchedule';
import { resolveBatchScheduleRecipe } from '../../shared/types/BatchSchedule';
import {
  resolveActiveRoomClimate,
  type ClimatePeriod,
  type PartialRoomClimate,
} from '../utils/climateTargetResolver';

// Climate targets for a room — aligned with shared TargetRange { min, optimal, max }
export interface ClimateTargets {
  temperature?: { min: number; optimal?: number; max: number };
  humidity?: { min: number; optimal?: number; max: number };
  vpd?: { min: number; optimal?: number; max: number };
  co2?: { min: number; optimal?: number; max: number };
  lightPercent?: number;
  lightHours?: number;
  // Day/night sub-targets (new SSOT structure)
  day?: {
    temperature?: { min: number; optimal?: number; max: number };
    humidity?: { min: number; optimal?: number; max: number };
    vpd?: { min: number; optimal?: number; max: number };
    co2?: { min: number; optimal?: number; max: number };
  };
  night?: {
    temperature?: { min: number; optimal?: number; max: number };
    humidity?: { min: number; optimal?: number; max: number };
    vpd?: { min: number; optimal?: number; max: number };
    co2?: { min: number; optimal?: number; max: number };
  };
}

// Room category types
export type RoomCategory = 'production' | 'technological' | 'dry';

// Room sub-types for each category
export type ProductionRoomType = 'flower' | 'veg' | 'mother' | 'clone';
export type TechnologicalRoomType = 'ro' | 'valve_control' | 'hvac' | 'general';
export type DryRoomType = 'drying' | 'curing';

// Types
export interface Room {
  id: string;
  name: string;
  status: 'healthy' | 'warning' | 'critical' | 'offline';
  color?: string;
  order?: number;
  climate?: ClimateTargets;  // Room-specific climate targets
  
  // Room categorization (SSOT - stored only here)
  category?: RoomCategory;  // 'production' | 'technological' | 'dry' - defaults to 'production'
  roomType?: string;        // Sub-type based on category
  
  // Tech room linking (SSOT - stored only in tech room)
  servesRoomIds?: string[]; // For tech rooms: which production rooms this serves
  
  // Per-room AI mode
  aiMode?: 'manual' | 'assisted' | 'autonomous';
  
  // Batch integration fields
  activeBatchId?: string;        // Linked batch from Elevia Lab
  activeBatchName?: string;      // Denormalized for display
  currentFloweringWeek?: number; // Current week (1-9)
  floweringStartDate?: Date;     // When flowering stage started
  batchTargetsSource?: 'manual' | 'batch' | 'ai';  // Where targets came from
  // Enhanced batch tracking fields
  dayOfFlower?: number;          // Exact day (1-63+)
  floweringPhase?: 'early' | 'mid' | 'late' | 'ripening';  // Current phase
  batchStage?: string;           // Full stage from Elevia Lab (Flowering, PreGrow_Production, etc.)
  batchStageDates?: Record<string, string>;  // Full stage history for AI
  isPreGrowInProduction?: boolean;  // PreGrow batch in production room
  expectedHarvestDate?: Date;    // Calculated harvest date
  daysUntilHarvest?: number;     // Remaining days
  targetFloweringDays?: number;  // Target flowering duration (default 63)
  // Pre-grow parity with flowering counters above. Mirrored from batch at
  // import time and written by the controller on schedule reconcile.
  pregrowDurationDays?: number;  // Per-batch pre-grow length, Elevia SSOT (default 21)
  dayOfPregrow?: number;         // Days since stageDates.PreGrow_Production
  daysUntilFlowering?: number;   // pregrowDurationDays - dayOfPregrow
  batchLastSyncedAt?: Date;      // Last sync timestamp
  batchScheduleMode?: BatchScheduleMode;
  activeTargetStage?: ActiveTargetStage;
  activeRecipeKey?: ActiveRecipeKey;
  currentTargetWeek?: number;
  schedulePinnedWeek?: number;
  scheduleActivationId?: string;
  scheduleSnapshotId?: string;
  lastScheduleAppliedAt?: Date | string;
  lightState?: 'day' | 'night';
  lastScheduleValidationStatus?: 'valid' | 'warnings' | 'invalid';
  scheduleConflictStatus?: 'none' | 'warnings' | 'blocked';
  scheduleConflictIds?: string[];
  automationConfig?: BatchScheduleAutomationConfig;

  /** Dashboard display order for SDI-12 table sensors in this room. */
  soilSensorOrder?: string[];
  /** Irrigation Crop Steering program tab order for this room. */
  cropSteeringProgramOrder?: string[];
  /** Manual valve control card order for this room. */
  manualValveOrder?: string[];
}

export interface Device {
  id: string;
  name: string;
  type: 'relay' | 'analog' | 'dehumidifier' | 'fan' | 'light' | 'led' | 'pump' | 'heater' | 'cooler' | 'sensor' | 'sdi12_sensor' | 'co2_controller';
  roomId: string;
  channel: number;
  mode: 'auto' | 'on' | 'off';
  state: boolean;
  level?: number;
  lastUpdated?: Date;
  lastUpdatedBy?: string; // 'irrigation_scheduler', 'rule_engine', 'user', etc.
  // Cut relay for LED/analog devices (cuts AC power)
  cutRelay?: number; // Relay channel that cuts power (0 = none, 1-48 = relay channel)
  // Sensor-specific fields (for type='sensor' or 'sdi12_sensor')
  sensorType?: 'sdi12' | 'analog' | 'snmp';
  sensorAddress?: string;
  vendor?: string;
  model?: string;
  // SDI-12 sensor specific
  tableId?: string; // Table assignment for irrigation
  channels?: string[]; // ['vwc', 'ec', 'temperature']
  latestReading?: {
    vwc?: number;
    ec?: number;
    temperature?: number;
    timestamp?: Date;
  };
  status?: 'online' | 'offline' | 'error';
}

export interface Sensor {
  id: string;
  sensorId?: string; // Physical sensor ID (e.g., comet-192-168-1-210)
  name: string;
  roomId: string;
  type: 'temperature' | 'humidity' | 'co2' | 'vpd' | 'soil_moisture' | 'light_level';
  value: number;
  unit: string;
  lastUpdated?: Date;
  ipAddress?: string;
  status?: 'online' | 'offline';
}

export interface SensorReading {
  sensorId: string;
  roomId: string;
  value: number;
  unit: string;
  channel: string;
  timestamp: Date;
}

// Rule System Types (matching shared/types/Rule.ts)
export type RuleState = 'active' | 'standby' | 'disabled' | 'error' | 'oscillation_disabled';
export type RuleType = 'trigger' | 'schedule' | 'alert';
export type ConditionOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq' | 'between' | 'outside';
export type ConditionSource = 'sensor' | 'device' | 'time' | 'schedule';
export type ActionType = 'setDeviceState' | 'setDeviceLevel' | 'setDeviceMode' | 'sendAlert' | 'logEvent' | 'triggerRule';
export type ActivationPoint = 'above' | 'below';

export interface RuleCondition {
  id: string;
  source: ConditionSource;
  // For sensor conditions
  sensorId?: string;
  sensorType?: string;
  reading?: string;
  // For device conditions
  deviceId?: string;
  deviceProperty?: 'state' | 'mode' | 'level';
  // For time conditions
  timeStart?: string;
  timeEnd?: string;
  daysOfWeek?: number[];
  // Comparison
  operator: ConditionOperator;
  value: number | boolean | string;
  value2?: number;
  // Day/night-aware setpoints (optional)
  nightValue?: number | boolean | string;
  nightValue2?: number;
  // Deadband
  deadband?: number;
}

export interface RuleAction {
  id: string;
  type: ActionType;
  // For device actions
  deviceId?: string;
  deviceIds?: string[];
  state?: boolean;
  mode?: 'on' | 'off' | 'auto';
  level?: number;
  fadeInMinutes?: number;
  fadeOutMinutes?: number;
  // For alert actions
  alertSeverity?: 'warning' | 'critical';
  alertMessage?: string;
  // For rule triggers
  targetRuleId?: string;
  // Delay
  delayMs?: number;
}

export interface AlertConfig {
  sensor?: string;
  timeOfDay?: 'always' | 'day' | 'night' | 'custom';
  customStartHour?: number;
  customEndHour?: number;
  minimumDuration?: number;
  emails?: string[];
  pushNotifications?: boolean;
  persistentNotifications?: boolean;
}

export interface Rule {
  id: string;
  name: string;
  description?: string;
  type: RuleType;
  controllerId: string;
  roomId: string | null;
  enabled: boolean;
  state?: RuleState;
  lastTriggered?: Date;
  lastEvaluated?: Date;
  triggerCount?: number;
  recentTriggers?: number[];
  triggers24h?: number;
  triggersToday?: number;
  disabledReason?: string;
  lastError?: string;
  // Full condition/action structure
  conditions: RuleCondition[];
  actions: RuleAction[];
  reverseActions?: RuleAction[];
  // Schedule-specific
  schedule?: {
    startTime?: { hour: number; minute: number };
    endTime?: { hour: number; minute: number };
    daysOfWeek?: number[];
  };
  // Alert-specific
  alert?: AlertConfig;
  // Sensor trigger specific
  sensor?: string;
  sensorSource?: string;
  sensorId?: string | null;
  sensorLabel?: string;
  setpoint?: number;
  /** Optional separate night-time setpoint. When set, RuleEngine uses this
   *  instead of `setpoint`/condition.value whenever the room's lightState
   *  is 'night'. Persisted both at the rule root (for display) and inside
   *  the matching RuleCondition as `nightValue`. */
  nightSetpoint?: number | null;
  deadband?: number;
  activationPoint?: ActivationPoint;
  minRuntime?: number;
  alwaysActive?: boolean;
  activeTimeStart?: { hour: number; minute: number } | null;
  activeTimeEnd?: { hour: number; minute: number } | null;
  // Timing
  evaluationIntervalMs?: number;
  minTriggerIntervalMs?: number;
  // AI integration
  ai?: {
    canOverride: boolean;
    suggestImprovements: boolean;
  };
  // Priority
  priority?: number;
  // Metadata
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  managedBy?: string;
  managedPurpose?: string;
  batchId?: string;
  activationId?: string;
  cascadeStage?: number;
  dehuControlVersion?: 1 | 2;
  dehuControlMode?: 'staged_target';
  dehuSharedOffValue?: number;
  nightDehuSharedOffValue?: number;
  dehuStageOffset?: number;
  dehuMinDeadband?: number;
  dehuSetpointRh?: number;
  nightDehuSetpointRh?: number;
  dehuLeadOnRh?: number;
  dehuLeadOffRh?: number;
  nightDehuLeadOnRh?: number;
  nightDehuLeadOffRh?: number;
  dehuStageOnRh?: number;
  dehuStageOffRh?: number;
  nightDehuStageOnRh?: number;
  nightDehuStageOffRh?: number;
  dehuStageOnDelaySeconds?: number;
  dehuMinOnSeconds?: number;
  dehuMinOffSeconds?: number;
  dehuEmergencyMarginRh?: number;
  dehuEmergencyRh?: number;
  nightDehuEmergencyRh?: number;
  dehuMaxActiveStages?: number;
  nightDehuMaxActiveStages?: number;
  dehuTargetStage?: string;
  dehuTargetWeek?: number;
  dehuControlBandRh?: number;
  disabledByBatchSchedule?: boolean;
  disabledByActivationId?: string;
  // Legacy (backwards compat)
  condition?: string;
}

export interface ControllerStatus {
  id: string;
  name: string;
  status: 'online' | 'offline';
  lastHeartbeat?: Date;
  hardware?: string;
  version?: string;
  startedAt?: Date;
  systemMetrics?: {
    cpu: number;
    memoryMB: number;
    uptimeHours: number;
  };
}

// Controller ID - imported from single source of truth
import { CONTROLLER_ID } from '../firebase';

// ============================================
// Shared snapshot store (Phase 3 listener dedupe)
// ============================================
//
// Several hooks used to open their own onSnapshot on the exact same
// document/query (controller heartbeat doc, rooms, per-room devices/rules,
// sensor_registry). The store below ref-counts one real Firestore listener
// per key and fans the snapshots out to every mounted hook instance.
// Delivery stays lossless real-time: it is the same onSnapshot stream,
// just shared. When the last consumer unmounts, the listener is torn down,
// so remount-style retry gates still force a fresh subscription.

interface SharedSnapshotState<T> {
  value: T | undefined;
  loaded: boolean;
  error: Error | null;
}

interface SharedSnapshotEntry {
  state: SharedSnapshotState<unknown>;
  subscribers: Set<(state: SharedSnapshotState<unknown>) => void>;
  refs: number;
  unsubscribe: () => void;
}

const sharedSnapshotEntries = new Map<string, SharedSnapshotEntry>();

// Stable empty fallbacks so hook consumers keep referential equality while loading.
const EMPTY_ROOMS: Room[] = [];
const EMPTY_DEVICES: Device[] = [];
const EMPTY_RULES: Rule[] = [];
const EMPTY_REGISTRY_DOCS: Array<{ id: string; data: Record<string, unknown> }> = [];

function subscribeShared<T>(
  key: string,
  start: (emit: (value: T) => void, fail: (error: Error) => void) => () => void,
  onChange: (state: SharedSnapshotState<T>) => void,
): () => void {
  let entry = sharedSnapshotEntries.get(key);
  if (!entry) {
    const created: SharedSnapshotEntry = {
      state: { value: undefined, loaded: false, error: null },
      subscribers: new Set(),
      refs: 0,
      unsubscribe: () => {},
    };
    sharedSnapshotEntries.set(key, created);
    created.unsubscribe = start(
      (value) => {
        created.state = { value, loaded: true, error: null };
        created.subscribers.forEach(cb => cb(created.state));
      },
      (error) => {
        created.state = { ...created.state, loaded: true, error };
        created.subscribers.forEach(cb => cb(created.state));
      },
    );
    entry = created;
  }
  entry.refs += 1;
  const cb = onChange as (state: SharedSnapshotState<unknown>) => void;
  entry.subscribers.add(cb);
  // Replay the latest snapshot so late subscribers don't wait for the next one.
  if (entry.state.loaded) cb(entry.state);
  return () => {
    const current = sharedSnapshotEntries.get(key);
    if (!current) return;
    current.refs -= 1;
    current.subscribers.delete(cb);
    if (current.refs <= 0) {
      current.unsubscribe();
      sharedSnapshotEntries.delete(key);
    }
  };
}

function useSharedSnapshot<T>(
  key: string,
  start: (emit: (value: T) => void, fail: (error: Error) => void) => () => void,
): SharedSnapshotState<T> {
  const [state, setState] = useState<SharedSnapshotState<T>>({
    value: undefined,
    loaded: false,
    error: null,
  });
  // The starter closure may capture params (e.g. roomId) that are already part
  // of the key; keep it in a ref so the effect only re-runs when the key changes.
  const startRef = useRef(start);
  startRef.current = start;

  useEffect(() => {
    setState({ value: undefined, loaded: false, error: null });
    return subscribeShared<T>(key, (emit, fail) => startRef.current(emit, fail), setState);
  }, [key]);

  return state;
}

// --- Shared controller heartbeat doc (controllers/{CONTROLLER_ID}) ---
// Note: backend writes to 'controllers' (not COLLECTIONS.controllers which is 'ai_controllers')

const CONTROLLER_DOC_KEY = 'controller-doc';

function startControllerDocListener(
  emit: (value: Record<string, unknown> | null) => void,
  fail: (error: Error) => void,
): () => void {
  const docRef = doc(db, 'controllers', CONTROLLER_ID);
  return onSnapshot(
    docRef,
    (snapshot) => emit(snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null),
    (err) => {
      console.error('Controller doc error:', err);
      fail(err);
    },
  );
}

/**
 * Subscribe to the shared controller heartbeat doc from non-hook code
 * (used by useHAStatus so Settings doesn't open a second listener on the
 * same document the app layout already watches).
 */
export function subscribeControllerDocShared(
  onData: (data: Record<string, unknown> | null) => void,
): () => void {
  return subscribeShared<Record<string, unknown> | null>(
    CONTROLLER_DOC_KEY,
    startControllerDocListener,
    (state) => {
      if (state.loaded) onData(state.value ?? null);
    },
  );
}

/**
 * Hook for controller status
 */
export function useControllerStatus() {
  const shared = useSharedSnapshot<Record<string, unknown> | null>(
    CONTROLLER_DOC_KEY,
    startControllerDocListener,
  );

  const status = useMemo<ControllerStatus | null>(() => {
    const data = shared.value as Record<string, any> | null | undefined;
    if (!data) return null;
    return {
      id: CONTROLLER_ID,
      name: data.name || 'AI Controller',
      status: data.status || 'offline',
      lastHeartbeat: data.lastHeartbeat?.toDate?.(),
      hardware: data.capabilities?.hardware,
      version: data.version,
      startedAt: data.startedAt?.toDate?.(),
      systemMetrics: data.systemMetrics || undefined,
    };
  }, [shared.value]);

  return { status, loading: !shared.loaded, error: shared.error };
}

// --- Shared rooms query ---

const ROOMS_KEY = 'rooms';

function startRoomsListener(
  emit: (value: Room[]) => void,
  fail: (error: Error) => void,
): () => void {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTIONS.rooms),
    where('controllerId', '==', CONTROLLER_ID)
  );

  return onSnapshot(q,
    (snapshot) => {
      const roomList: Room[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            name: data.name || 'Unnamed Room',
            status: data.status || 'offline',
            color: data.color,
            order: data.order || 0,
            // Room categorization (SSOT)
            category: data.category || 'production',  // Default to production for existing rooms
            roomType: data.roomType,
            servesRoomIds: data.servesRoomIds || [],  // Tech rooms: which production rooms served
            // Include climate targets if available
            climate: data.climate ? {
              temperature: data.climate.temperature,
              humidity: data.climate.humidity,
              vpd: data.climate.vpd,
              co2: data.climate.co2,
              day: data.climate.day,
              night: data.climate.night,
              lightPercent: data.climate.lightPercent,
              lightHours: data.climate.lightHours,
            } : undefined,
            lightState: data.lightState,
            // Per-room AI mode
            aiMode: data.aiMode || 'manual',
            // Batch integration fields
            activeBatchId: data.activeBatchId,
            activeBatchName: data.activeBatchName,
            currentFloweringWeek: data.currentFloweringWeek,
            floweringStartDate: data.floweringStartDate?.toDate?.() || data.floweringStartDate,
            batchTargetsSource: data.batchTargetsSource,
            // Enhanced batch tracking fields
            dayOfFlower: data.dayOfFlower,
            floweringPhase: data.floweringPhase,
            batchStage: data.batchStage,
            batchStageDates: data.batchStageDates,
            isPreGrowInProduction: data.isPreGrowInProduction,
            expectedHarvestDate: data.expectedHarvestDate?.toDate?.() || data.expectedHarvestDate,
            daysUntilHarvest: data.daysUntilHarvest,
            targetFloweringDays: data.targetFloweringDays,
            pregrowDurationDays: data.pregrowDurationDays,
            dayOfPregrow: data.dayOfPregrow,
            daysUntilFlowering: data.daysUntilFlowering,
            batchLastSyncedAt: data.batchLastSyncedAt?.toDate?.() || data.batchLastSyncedAt,
            batchScheduleMode: data.batchScheduleMode,
            activeTargetStage: data.activeTargetStage,
            activeRecipeKey: data.activeRecipeKey,
            currentTargetWeek: data.currentTargetWeek,
            schedulePinnedWeek: data.schedulePinnedWeek,
            scheduleActivationId: data.scheduleActivationId,
            scheduleSnapshotId: data.scheduleSnapshotId,
            lastScheduleAppliedAt: data.lastScheduleAppliedAt?.toDate?.() || data.lastScheduleAppliedAt,
            lastScheduleValidationStatus: data.lastScheduleValidationStatus,
            scheduleConflictStatus: data.scheduleConflictStatus,
            scheduleConflictIds: data.scheduleConflictIds,
            automationConfig: data.automationConfig,
            soilSensorOrder: data.soilSensorOrder || [],
            cropSteeringProgramOrder: data.cropSteeringProgramOrder || [],
            manualValveOrder: data.manualValveOrder || [],
          };
        });
      emit(roomList);
    },
    (err) => {
      console.error('Rooms error:', err);
      fail(err);
    }
  );
}

/**
 * Write-only room update. Components that only need to WRITE room fields
 * (e.g. ProductionRoomCard toggling aiMode) should use this instead of
 * useRooms(), which would open/share a rooms listener they don't read.
 */
export async function updateRoomDoc(roomId: string, updates: Partial<Room>): Promise<void> {
  const roomRef = doc(db, COLLECTIONS.rooms, roomId);
  await updateDoc(roomRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Hook for rooms
 */
export function useRooms() {
  const shared = useSharedSnapshot<Room[]>(ROOMS_KEY, startRoomsListener);
  const rooms = shared.value ?? EMPTY_ROOMS;

  const addRoom = useCallback(async (
    name: string, 
    category: RoomCategory = 'production',
    roomType?: string,
    servesRoomIds?: string[]
  ) => {
    await addDoc(collection(db, COLLECTIONS.rooms), {
      name,
      controllerId: CONTROLLER_ID,
      status: 'healthy',
      order: rooms.length,
      category,
      roomType,
      servesRoomIds: servesRoomIds || [],
      createdAt: serverTimestamp(),
    });
  }, [rooms.length]);

  const updateRoom = useCallback(updateRoomDoc, []);

  const deleteRoom = useCallback(async (roomId: string) => {
    const roomRef = doc(db, COLLECTIONS.rooms, roomId);
    await deleteDoc(roomRef);
  }, []);

  return { rooms, loading: !shared.loaded, error: shared.error, addRoom, updateRoom, deleteRoom };
}

/**
 * Hook for tables (irrigation zones within rooms)
 * Tables connect sensors to valves for automated irrigation control.
 */
export function useTables(roomId?: string) {
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const tablesRef = collection(db, 'ai_controllers', CONTROLLER_ID, 'tables');
    
    const q = roomId
      ? query(tablesRef, where('roomId', '==', roomId))
      : query(tablesRef);

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const tableList: Table[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: (doc.data().createdAt as Timestamp)?.toDate(),
          updatedAt: (doc.data().updatedAt as Timestamp)?.toDate(),
        } as Table));
        setTables(tableList);
        setLoading(false);
      },
      (err) => {
        console.error('Tables error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [roomId]);

  const createTable = useCallback(async (data: {
    name: string;
    roomId: string;
    valveDeviceId: string;
    position?: number;
  }) => {
    const tablesRef = collection(db, 'ai_controllers', CONTROLLER_ID, 'tables');
    const newTable = {
      name: data.name,
      roomId: data.roomId,
      valveDeviceId: data.valveDeviceId,
      sensorIds: [],
      position: data.position ?? tables.length,
      irrigationConfig: {
        enabled: false,
        mode: 'vwc',
        vwcStart: 30,
        vwcStop: 60,
        minRunSeconds: 30,
        maxRunSeconds: 300,
        cooldownMinutes: 15,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const docRef = await addDoc(tablesRef, newTable);
    return docRef.id;
  }, [tables.length]);

  const updateTable = useCallback(async (tableId: string, updates: Partial<Table>) => {
    const tableRef = doc(db, 'ai_controllers', CONTROLLER_ID, 'tables', tableId);
    await updateDoc(tableRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  }, []);

  const deleteTable = useCallback(async (tableId: string) => {
    const tableRef = doc(db, 'ai_controllers', CONTROLLER_ID, 'tables', tableId);
    await deleteDoc(tableRef);
  }, []);

  const addSensorToTable = useCallback(async (tableId: string, sensorId: string) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) throw new Error('Table not found');
    
    if (!table.sensorIds.includes(sensorId)) {
      await updateTable(tableId, {
        sensorIds: [...table.sensorIds, sensorId],
      });
    }
    
    // Update device's tableId and roomId
    const deviceRef = doc(db, 'controllers', CONTROLLER_ID, 'devices', sensorId);
    await updateDoc(deviceRef, {
      tableId,
      roomId: table.roomId,
      updatedAt: serverTimestamp(),
    });
  }, [tables, updateTable]);

  const removeSensorFromTable = useCallback(async (tableId: string, sensorId: string) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) throw new Error('Table not found');
    
    await updateTable(tableId, {
      sensorIds: table.sensorIds.filter(id => id !== sensorId),
    });
    
    // Clear device's tableId
    const deviceRef = doc(db, 'controllers', CONTROLLER_ID, 'devices', sensorId);
    await updateDoc(deviceRef, {
      tableId: null,
      updatedAt: serverTimestamp(),
    });
  }, [tables, updateTable]);

  return { 
    tables, 
    loading, 
    error, 
    createTable, 
    updateTable, 
    deleteTable,
    addSensorToTable,
    removeSensorFromTable,
  };
}

// Table interface for the hook
export interface Table {
  id: string;
  name: string;
  roomId: string;
  valveDeviceId: string;
  sensorIds: string[];
  position?: number;
  irrigationConfig: {
    enabled: boolean;
    mode: 'schedule' | 'vwc' | 'both';
    vwcStart: number;
    vwcStop: number;
    minRunSeconds: number;
    maxRunSeconds: number;
    cooldownMinutes: number;
  };
  cropSteering?: {
    enabled: boolean;
    mode: 'generative' | 'vegetative' | 'ripening' | 'custom';
    targetDryback: number;
    p1DelayMinutes: number;
    p1TriggerVWC: number;
    vwcFloor: number;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Hook for devices (optionally filtered by room)
 * Reads from controllers/{CONTROLLER_ID}/devices subcollection (production pattern)
 */
export function useDevices(roomId?: string) {
  // Shared per-key listener: every consumer of the same roomId (or "all")
  // shares one onSnapshot. Per-room server-side filtering is preserved —
  // the key includes the roomId and the query still uses where('roomId','==',...).
  const shared = useSharedSnapshot<Device[]>(
    `devices:${roomId ?? '__all__'}`,
    (emit, fail) => {
      // Read from production-style subcollection: controllers/{controllerId}/devices
      const devicesCollectionRef = collection(db, 'controllers', CONTROLLER_ID, 'devices');

      const q = roomId
        ? query(devicesCollectionRef, where('roomId', '==', roomId))
        : query(devicesCollectionRef);

      return onSnapshot(q,
        (snapshot) => {
          const deviceList: Device[] = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || `Device ${doc.id}`,
              type: data.type || 'relay',
              roomId: data.roomId || '',
              channel: data.channel || data.hardware?.channel || 0,
              mode: data.mode || 'auto',
              state: data.state ?? false,
              level: data.level,
              lastUpdated: data.updatedAt?.toDate?.() || data.lastUpdated?.toDate?.(),
              lastUpdatedBy: data.lastUpdatedBy,
              // Read cut relay config from hardware object (where controller expects it)
              cutRelay: data.hardware?.cutRelay || data.cutRelay,
              // SDI-12 sensor table assignment
              tableId: data.tableId,
            };
          });
          emit(deviceList);
        },
        (err) => {
          console.error('Devices error:', err);
          fail(err);
        }
      );
    },
  );
  const devices = shared.value ?? EMPTY_DEVICES;

  const levelDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const setDeviceMode = useCallback(async (deviceId: string, mode: 'auto' | 'on' | 'off') => {
    // Find the device to get its current level (for light devices)
    const device = devices.find(d => d.id === deviceId);
    const isLightDevice = device?.type === 'light' || device?.type === 'analog';
    
    // Build update data - sync with controller hardware behavior
    const updateData: Record<string, unknown> = {
      mode,
      state: mode === 'on',
      updatedAt: serverTimestamp(),
    };
    
    // For light devices: sync level with mode (matches controller behavior)
    // ON cannot be 0% (0% = OFF), minimum on level is 1%
    if (isLightDevice) {
      if (mode === 'off') {
        // OFF = level 0 (matches controller writeAnalog(channel, 0))
        updateData.level = 0;
      } else if (mode === 'on' && (device?.level === undefined || device.level === 0)) {
        // ON from OFF = minimum 1% (gentle start, user adjusts from there)
        updateData.level = 1;
      }
    }
    
    // Update device in production-style subcollection
    const deviceRef = doc(db, 'controllers', CONTROLLER_ID, 'devices', deviceId);
    await updateDoc(deviceRef, updateData);
    
    // Build command payload
    const commandData: Record<string, unknown> = {
      type: 'device',
      deviceId,
      mode,
      state: mode === 'on',
      status: 'pending',
      createdAt: serverTimestamp(),
    };
    
    // Include level in command for light devices (matches controller behavior)
    if (isLightDevice) {
      if (mode === 'off') {
        commandData.level = 0;
      } else if (mode === 'on') {
        // Use current level if > 0, otherwise minimum 1% (gentle start)
        commandData.level = (device?.level && device.level > 0) ? device.level : 1;
      }
    }
    
    await sendControllerCommand({
      ...commandData,
      roomId: device?.roomId ?? null,
    });
  }, [devices]);

  const setDeviceLevel = useCallback((deviceId: string, level: number) => {
    // Debounce: only send the command after 300ms of no further slider changes.
    // Prevents flooding the Wago PLC with dozens of writes/second during slider drag.
    const existing = levelDebounceTimers.current.get(deviceId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(async () => {
      levelDebounceTimers.current.delete(deviceId);

      const device = devices.find(d => d.id === deviceId);
      const deviceRef = doc(db, 'controllers', CONTROLLER_ID, 'devices', deviceId);

      const updateData: Record<string, unknown> = {
        level,
        updatedAt: serverTimestamp(),
      };

      if (device?.mode === 'off' && level > 0) {
        updateData.mode = 'on';
        updateData.state = true;
      }

      if (level === 0 && device?.mode !== 'auto') {
        updateData.mode = 'off';
        updateData.state = false;
      }

      await updateDoc(deviceRef, updateData);

      const commandData: Record<string, unknown> = {
        type: 'device',
        deviceId,
        level,
        status: 'pending',
        createdAt: serverTimestamp(),
      };

      if (device?.mode === 'off' && level > 0) {
        commandData.mode = 'on';
        commandData.state = true;
      } else if (level === 0 && device?.mode !== 'auto') {
        commandData.mode = 'off';
        commandData.state = false;
      }

      await sendControllerCommand({
        ...commandData,
        roomId: device?.roomId ?? null,
      });
    }, 300);

    levelDebounceTimers.current.set(deviceId, timer);
  }, [devices]);

  return { devices, loading: !shared.loaded, error: shared.error, setDeviceMode, setDeviceLevel };
}

/**
 * Hook for sensors (optionally filtered by room)
 * Reads from controllers/{CONTROLLER_ID}/sensor_registry subcollection (production pattern)
 */
export function useSensors(roomId?: string) {
  // One shared listener on the whole (small) sensor_registry; room filtering is
  // client-side. Previously every useRoomClimate consumer (each dashboard room
  // card, climate pages) opened its own where('room','==',roomId) registry query.
  const shared = useSharedSnapshot<RegistryDoc[]>(SENSOR_REGISTRY_KEY, startSensorRegistryListener);

  const sensors = useMemo<Sensor[]>(() => {
    if (!shared.value) return [];
    const sensorList: Sensor[] = [];

    for (const { id, data: raw } of shared.value) {
          const data = raw as Record<string, any>;
          if (roomId && String(data.room || '') !== roomId) continue;
          const sensorId = id;
          const baseName = data.name || `Comet Sensor`;
          const room = data.room || '';
          const lastUpdated = data.lastSeen?.toDate?.() || data.updatedAt?.toDate?.();
          const ipSuffix = data.ipAddress?.split('.').pop() || '';
          
          // Create individual sensor entries for each reading type (production stores all on one doc)
          if (data.temperature !== undefined && data.temperature !== null) {
            sensorList.push({
              id: `${sensorId}-temperature`,
              sensorId,
              name: baseName || `Comet Sensor .${ipSuffix}`,
              roomId: room,
              type: 'temperature',
              value: data.temperature,
              unit: '°C',
              lastUpdated,
              ipAddress: data.ipAddress,
              status: data.status || 'online',
            });
          }
          if (data.humidity !== undefined && data.humidity !== null) {
            sensorList.push({
              id: `${sensorId}-humidity`,
              sensorId,
              name: baseName || `Comet Sensor .${ipSuffix}`,
              roomId: room,
              type: 'humidity',
              value: data.humidity,
              unit: '%',
              lastUpdated,
              ipAddress: data.ipAddress,
              status: data.status || 'online',
            });
          }
          if (data.co2 !== undefined && data.co2 !== null) {
            sensorList.push({
              id: `${sensorId}-co2`,
              sensorId,
              name: baseName || `Comet Sensor .${ipSuffix}`,
              roomId: room,
              type: 'co2',
              value: data.co2,
              unit: 'ppm',
              // Per-metric staleness: a Comet can stay online (fresh lastSeen from
              // temp/humidity) while its CO2 element is dead. co2UpdatedAt is the
              // last GOOD CO2 write, so a frozen value shows as stale, not "Live".
              lastUpdated: data.co2UpdatedAt?.toDate?.() || lastUpdated,
              ipAddress: data.ipAddress,
              status: data.status || 'online',
            });
          }
          if (data.vpd !== undefined && data.vpd !== null) {
            sensorList.push({
              id: `${sensorId}-vpd`,
              sensorId,
              name: baseName || `Comet Sensor .${ipSuffix}`,
              roomId: room,
              type: 'vpd',
              value: data.vpd,
              unit: 'kPa',
              lastUpdated,
              ipAddress: data.ipAddress,
              status: data.status || 'online',
            });
          }
    }

    return sensorList;
  }, [shared.value, roomId]);

  return { sensors, loading: !shared.loaded, error: shared.error };
}

// =============================================================================
// Soil Sensor Data (SDI-12) - reads from sensor_registry (SSOT)
// =============================================================================

/** `ec` is pore-water EC (`ecBasis`, `bulkEc`: see registrySoilEc). */
export interface SoilSensorData extends RegistrySoilEc {
  sensorId: string;
  name: string;
  vwc: number | null;
  soilTemp: number | null;
  lastSeen: Date | null;
  status: 'online' | 'stale' | 'offline';
}

const STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

// --- Shared sensor_registry listener ---
// One listener on the whole (small) registry, shared by useSoilSensors (all
// room cards) and useSensorSummary. Previously each production room card
// opened its own where('room','==',roomId) query on the same collection.

const SENSOR_REGISTRY_KEY = 'sensor-registry';

interface RegistryDoc {
  id: string;
  data: Record<string, unknown>;
}

function startSensorRegistryListener(
  emit: (value: RegistryDoc[]) => void,
  fail: (error: Error) => void,
): () => void {
  const registryRef = collection(db, 'controllers', CONTROLLER_ID, 'sensor_registry');
  return onSnapshot(
    registryRef,
    (snapshot) => {
      emit(snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        data: docSnap.data() as Record<string, unknown>,
      })));
    },
    (err) => {
      console.error('Sensor registry error:', err);
      fail(err);
    },
  );
}

/**
 * Hook for soil sensors (SDI-12) in a room.
 * Reads from sensor_registry (SSOT) - the single source of truth for live readings.
 * Returns grouped readings per sensor (vwc + ec + soilTemp together).
 *
 * sensor_registry stores ai_rooms doc IDs in the `room` field (resolved at the
 * backend boundary by sdi12Service.resolveRoomId). No slug conversion needed here.
 * Room filtering happens client-side on the shared registry snapshot.
 */
export function useSoilSensors(roomId: string) {
  const shared = useSharedSnapshot<RegistryDoc[]>(SENSOR_REGISTRY_KEY, startSensorRegistryListener);

  const sensors = useMemo<SoilSensorData[]>(() => {
    if (!roomId || !shared.value) return [];
    const soilList: SoilSensorData[] = [];
    const now = Date.now();

    for (const { id, data: raw } of shared.value) {
      const data = raw as Record<string, any>;

      // Only SDI-12 soil sensors in this room (registry also holds climate sensors)
      if (data.type !== 'sdi12') continue;
      if (String(data.room || '') !== roomId) continue;

      const lastSeen = data.lastSeen?.toDate?.() || data.updatedAt?.toDate?.() || null;
      const msSinceUpdate = lastSeen ? now - lastSeen.getTime() : Infinity;

      let status: SoilSensorData['status'] = 'online';
      if (!lastSeen || data.status === 'offline') {
        status = 'offline';
      } else if (msSinceUpdate > STALE_THRESHOLD_MS) {
        status = 'stale';
      }

      soilList.push({
        sensorId: id,
        name: data.name || `Sensor ${data.sensorAddress || id}`,
        vwc: data.vwc ?? null,
        ...registrySoilEc(data),
        soilTemp: data.soil_temp ?? data.temperature ?? null,
        lastSeen,
        status,
      });
    }

    return soilList;
  }, [shared.value, roomId]);

  return { sensors, loading: roomId ? !shared.loaded : false };
}

/**
 * @deprecated This hook queries Firestore ai_sensor_data which is being replaced by InfluxDB.
 * Use getChartData() from services/chartDataService.ts instead for chart/graph data.
 * This hook is unused and will be removed after the InfluxDB migration is validated.
 */
export function useSensorHistory(sensorId: string, hours: number = 24) {
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cutoff = new Date();
    cutoff.setHours(cutoff.getHours() - hours);

    const q = query(
      collection(db, COLLECTIONS.sensorData),
      where('sensorId', '==', sensorId),
      where('timestamp', '>=', Timestamp.fromDate(cutoff)),
      orderBy('timestamp', 'desc'),
      limit(500)
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const data: SensorReading[] = snapshot.docs.map(doc => {
          const d = doc.data();
          return {
            sensorId: d.sensorId,
            roomId: d.roomId || '',
            value: d.value,
            unit: d.unit,
            channel: d.channel,
            timestamp: d.timestamp?.toDate?.() || new Date(),
          };
        });
        setReadings(data.reverse()); // Oldest first
        setLoading(false);
      },
      (err) => {
        console.error('Sensor history error:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [sensorId, hours]);

  return { readings, loading };
}

/**
 * Hook for rules (optionally filtered by room)
 * Reads from controllers/{CONTROLLER_ID}/rules subcollection (production pattern)
 */
export function useRules(roomId?: string) {
  // Shared per-key listener (see shared snapshot store above).
  const shared = useSharedSnapshot<Rule[]>(
    `rules:${roomId ?? '__all__'}`,
    (emit, fail) => {
      // Read from production-style subcollection: controllers/{controllerId}/rules
      const rulesCollectionRef = collection(db, 'controllers', CONTROLLER_ID, 'rules');

      const q = roomId
        ? query(rulesCollectionRef, where('roomId', '==', roomId))
        : query(rulesCollectionRef);

      return onSnapshot(q,
      (snapshot) => {
        const ruleList: Rule[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || 'Unnamed Rule',
            description: data.description,
            type: data.type || 'trigger',
            controllerId: data.controllerId || CONTROLLER_ID,
            roomId: data.roomId || null,
            enabled: data.enabled ?? true,
            state: data.state || 'standby',
            lastTriggered: data.lastTriggered?.toDate?.(),
            lastEvaluated: data.lastEvaluated?.toDate?.(),
            triggerCount: data.triggerCount || 0,
            recentTriggers: data.recentTriggers,
            triggers24h: data.triggers24h,
            triggersToday: data.triggersToday,
            // Full condition/action structure
            conditions: data.conditions || [],
            actions: data.actions || [],
            reverseActions: data.reverseActions,
            // Schedule-specific
            schedule: data.schedule,
            // Alert-specific
            alert: data.alert,
            // Sensor trigger specific
            sensor: data.sensor,
            sensorSource: data.sensorSource,
            sensorId: data.sensorId,
            sensorLabel: data.sensorLabel,
            setpoint: data.setpoint,
            deadband: data.deadband,
            activationPoint: data.activationPoint,
            minRuntime: data.minRuntime,
            alwaysActive: data.alwaysActive,
            activeTimeStart: data.activeTimeStart,
            activeTimeEnd: data.activeTimeEnd,
            // Timing
            evaluationIntervalMs: data.evaluationIntervalMs,
            minTriggerIntervalMs: data.minTriggerIntervalMs,
            // AI integration
            ai: data.ai || { canOverride: true, suggestImprovements: true },
            // Priority
            priority: data.priority || 0,
            // Metadata
            createdAt: data.createdAt?.toDate?.(),
            updatedAt: data.updatedAt?.toDate?.(),
            createdBy: data.createdBy,
            managedBy: data.managedBy,
            managedPurpose: data.managedPurpose,
            batchId: data.batchId,
            activationId: data.activationId,
            cascadeStage: data.cascadeStage,
            dehuControlVersion: data.dehuControlVersion,
            dehuControlMode: data.dehuControlMode,
            dehuSharedOffValue: data.dehuSharedOffValue,
            nightDehuSharedOffValue: data.nightDehuSharedOffValue,
            dehuStageOffset: data.dehuStageOffset,
            dehuMinDeadband: data.dehuMinDeadband,
            dehuSetpointRh: data.dehuSetpointRh,
            nightDehuSetpointRh: data.nightDehuSetpointRh,
            dehuLeadOnRh: data.dehuLeadOnRh,
            dehuLeadOffRh: data.dehuLeadOffRh,
            nightDehuLeadOnRh: data.nightDehuLeadOnRh,
            nightDehuLeadOffRh: data.nightDehuLeadOffRh,
            dehuStageOnRh: data.dehuStageOnRh,
            dehuStageOffRh: data.dehuStageOffRh,
            nightDehuStageOnRh: data.nightDehuStageOnRh,
            nightDehuStageOffRh: data.nightDehuStageOffRh,
            dehuStageOnDelaySeconds: data.dehuStageOnDelaySeconds,
            dehuMinOnSeconds: data.dehuMinOnSeconds,
            dehuMinOffSeconds: data.dehuMinOffSeconds,
            dehuEmergencyMarginRh: data.dehuEmergencyMarginRh,
            dehuEmergencyRh: data.dehuEmergencyRh,
            nightDehuEmergencyRh: data.nightDehuEmergencyRh,
            dehuMaxActiveStages: data.dehuMaxActiveStages,
            nightDehuMaxActiveStages: data.nightDehuMaxActiveStages,
            dehuTargetStage: data.dehuTargetStage,
            dehuTargetWeek: data.dehuTargetWeek,
            dehuControlBandRh: data.dehuControlBandRh,
            disabledByBatchSchedule: data.disabledByBatchSchedule,
            disabledByActivationId: data.disabledByActivationId,
            // Legacy (backwards compat)
            condition: data.condition,
          };
        });
        emit(ruleList);
      },
      (err) => {
        console.error('Rules error:', err);
        fail(err);
      }
      );
    },
  );
  const rules = shared.value ?? EMPTY_RULES;

  const toggleRule = useCallback(async (ruleId: string, enabled: boolean) => {
    const ruleRef = doc(db, 'controllers', CONTROLLER_ID, 'rules', ruleId);
    await updateDoc(ruleRef, { enabled, updatedAt: serverTimestamp() });
  }, []);

  const deleteRule = useCallback(async (ruleId: string) => {
    const ruleRef = doc(db, 'controllers', CONTROLLER_ID, 'rules', ruleId);
    await deleteDoc(ruleRef);
  }, []);

  const updateRuleRoom = useCallback(async (ruleId: string, roomId: string | null) => {
    const ruleRef = doc(db, 'controllers', CONTROLLER_ID, 'rules', ruleId);
    await updateDoc(ruleRef, { roomId, updatedAt: serverTimestamp() });
  }, []);

  return { rules, loading: !shared.loaded, error: shared.error, toggleRule, deleteRule, updateRuleRoom };
}

// Climate sensor data with source and target info
export interface ClimateReading {
  value: number | null;
  source: string;
  targetMin?: number;
  targetOptimal?: number;
  targetMax?: number;
}

export interface RoomClimateData {
  temperature: ClimateReading;
  humidity: ClimateReading;
  co2: ClimateReading;
  vpd: ClimateReading;
  /** Active day/night period used to resolve targets. */
  activePeriod: ClimatePeriod;
}

export interface UseRoomClimateOptions {
  /** Override active period (e.g. Climate page day/night toggle). */
  period?: ClimatePeriod;
}

/**
 * Hook for room climate summary (aggregated sensor data with source and targets)
 * Fetches room-specific targets from Firestore, falls back to shared defaults
 */
export function useRoomClimate(roomId: string, options?: UseRoomClimateOptions): RoomClimateData {
  const { sensors } = useSensors(roomId);

  // Room targets + light state come from the shared rooms listener (one query
  // app-wide) instead of a per-room doc listener per consumer. Every dashboard
  // room card used to open its own onSnapshot on its room doc here.
  const sharedRooms = useSharedSnapshot<Room[]>(ROOMS_KEY, startRoomsListener);
  const room = useMemo(
    () => (roomId ? sharedRooms.value?.find(r => r.id === roomId) : undefined),
    [sharedRooms.value, roomId],
  );

  const roomClimate = room?.climate as PartialRoomClimate | undefined;
  const lightState: ClimatePeriod | undefined =
    room?.lightState === 'night' ? 'night' : room?.lightState === 'day' ? 'day' : undefined;
  const roomCategory = room?.category;
  const roomType = room?.roomType;

  const { period: activePeriod, targets: activeTargets } = resolveActiveRoomClimate(roomClimate, {
    period: options?.period,
    lightState,
    category: roomCategory,
    roomType,
  });

  const getSensorData = (type: 'temperature' | 'humidity' | 'co2' | 'vpd'): ClimateReading => {
    const sensor = sensors.find(s => s.type === type);
    const metricTargets = activeTargets[type];
    return {
      value: sensor?.value ?? null,
      source: sensor?.status === 'online' ? 'SNMP' : 'N/A',
      targetMin: metricTargets.min,
      targetOptimal: metricTargets.optimal,
      targetMax: metricTargets.max,
    };
  };
  
  const temperature = getSensorData('temperature');
  const humidity = getSensorData('humidity');
  const co2 = getSensorData('co2');
  let vpd = getSensorData('vpd');

  // Calculate VPD if not available but we have temp and humidity
  if (vpd.value === null && temperature.value !== null && humidity.value !== null) {
    const svp = 0.6108 * Math.exp((17.27 * temperature.value) / (temperature.value + 237.3));
    const avp = (humidity.value / 100) * svp;
    vpd = {
      ...vpd,
      value: parseFloat((svp - avp).toFixed(2)),
      source: 'Calculated',
    };
  }

  return { temperature, humidity, co2, vpd, activePeriod };
}

/**
 * Hook for fetching batch targets from SSOT (batches collection)
 * Uses room's activeBatchId and currentFloweringWeek to get current targets
 * 
 * SSOT: Targets come directly from batch document, never copied
 */
export interface BatchTargets {
  temperature?: number;
  humidity?: number;
  vpd?: number;
  co2?: number;
  lightPercent?: number;
  week?: number;
  /** Resolved stage used to pick the recipe (pregrow_production | flowering). */
  stage?: ActiveTargetStage;
  /** Which source field provided the targets (climateRecipe, pregrowClimateRecipe, or floweringWeekSettings). */
  recipeKey?: ActiveRecipeKey;
}

export interface UseBatchTargetsResult {
  targets: BatchTargets | null;
  loading: boolean;
  error: Error | null;
  hasBatch: boolean;
}

export function useBatchTargets(room: Room | null): UseBatchTargetsResult {
  const [targets, setTargets] = useState<BatchTargets | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!room?.activeBatchId) {
      setTargets(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Subscribe to batch document for real-time target updates.
    // Targets are resolved using the same shared helper as the controller
    // (resolveBatchScheduleRecipe), prioritizing activeTargetStage +
    // currentTargetWeek + climateRecipe / pregrowClimateRecipe over the
    // legacy floweringWeekSettings array. This keeps the dashboard in sync
    // with the active recipe even after switching from pre-grow to flower.
    const batchRef = doc(db, 'batches', room.activeBatchId);
    const unsubscribe = onSnapshot(batchRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setTargets(null);
          setLoading(false);
          return;
        }

        const batchData = snapshot.data() as Record<string, unknown>;

        try {
          const resolution = resolveBatchScheduleRecipe({
            batchStage: room.batchStage,
            targetStage: room.activeTargetStage,
            targetWeek: room.currentTargetWeek,
            currentFloweringWeek: room.currentFloweringWeek,
            schedulePinnedWeek: room.schedulePinnedWeek,
            pregrowClimateRecipe: batchData.pregrowClimateRecipe as Parameters<typeof resolveBatchScheduleRecipe>[0]['pregrowClimateRecipe'],
            climateRecipe: batchData.climateRecipe as Parameters<typeof resolveBatchScheduleRecipe>[0]['climateRecipe'],
            floweringWeekSettings: batchData.floweringWeekSettings as Parameters<typeof resolveBatchScheduleRecipe>[0]['floweringWeekSettings'],
          });

          const day = resolution.recipe.day;
          setTargets({
            temperature: day.temperature.optimal,
            humidity: day.humidity.optimal,
            vpd: day.vpd.optimal,
            co2: day.co2.optimal,
            lightPercent: resolution.recipe.lightPercent,
            week: resolution.effectiveWeek,
            stage: resolution.targetStage,
            recipeKey: resolution.recipeKey,
          });
        } catch (err) {
          // No recipe data available; fall back to legacy floweringWeekSettings lookup.
          const floweringWeekSettings = (batchData.floweringWeekSettings as Array<{ week: number; temperature: number; humidity: number; vpd?: number; co2: number; lightPercent?: number }> | undefined) || [];
          const currentWeek = room.currentTargetWeek || room.currentFloweringWeek || 1;
          const weekSettings = floweringWeekSettings.find((s) => s.week === currentWeek);
          if (weekSettings) {
            setTargets({
              temperature: weekSettings.temperature,
              humidity: weekSettings.humidity,
              vpd: weekSettings.vpd,
              co2: weekSettings.co2,
              lightPercent: weekSettings.lightPercent,
              week: currentWeek,
              stage: room.activeTargetStage ?? 'flowering',
              recipeKey: 'floweringWeekSettings',
            });
          } else {
            setTargets(null);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching batch targets:', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [
    room?.activeBatchId,
    room?.activeTargetStage,
    room?.activeRecipeKey,
    room?.currentTargetWeek,
    room?.currentFloweringWeek,
    room?.schedulePinnedWeek,
    room?.batchStage,
  ]);

  return { 
    targets, 
    loading, 
    error, 
    hasBatch: !!room?.activeBatchId 
  };
}

// ============================================
// Additional Types
// ============================================

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'debug';
  category: string;
  message: string;
  details?: Record<string, any>;
  correlationId?: string;
  
  // Entity IDs for filtering
  roomId?: string;
  deviceId?: string;
  ruleId?: string;
  sensorId?: string;
  
  // Human-readable names (extracted from details or looked up)
  roomName?: string;
  deviceName?: string;
  ruleName?: string;
  sensorName?: string;
  
  // Event type (e.g., 'rule_enabled', 'device_command')
  action?: string;
  
  // Human-readable source info
  source?: {
    type: 'user' | 'rule' | 'schedule' | 'ai' | 'system' | 'api';
    name: string;
  };
  
  // Hardware context
  hardware?: {
    type: string;
    ip?: string;
    channel?: number;
    slot?: number;
    physicalLabel?: string;
  };
  
  // Sensor snapshot at time of event
  sensorSnapshot?: {
    temperature?: number;
    humidity?: number;
    vpd?: number;
    co2?: number;
  };
  
  // Timing metrics
  timing?: {
    latencyMs?: number;
    totalDurationMs?: number;
  };
}

export interface Batch {
  id: string;
  name: string;
  strain: string;
  roomId: string;
  phase: 'germination' | 'seedling' | 'vegetative' | 'flowering' | 'harvest' | 'drying' | 'curing';
  startDate: Date;
  expectedHarvest?: Date;
  status: 'active' | 'completed' | 'archived';
}

export interface TelegramRecipient {
  id: string;
  label: string;
  chatId: string;
}

export interface NotificationChannelConfig {
  telegram: { enabled: boolean; botToken?: string; chatId?: string; recipients?: TelegramRecipient[] };
  email: {
    enabled: boolean;
    smtp?: { host: string; port: number; secure: boolean; user: string; pass: string; from: string };
    recipients?: string[];
  };
  webhook: { enabled: boolean; urls?: string[] };
  push: { enabled: boolean };
}

export type AlertCategory = 'climate' | 'sensor' | 'device' | 'infrastructure' | 'system' | 'ai';

export interface NotificationRouting {
  climate:        { warning: string[]; critical: string[] };
  sensor:         { warning: string[]; critical: string[] };
  device:         { warning: string[]; critical: string[] };
  infrastructure: { warning: string[]; critical: string[] };
  system:         { warning: string[]; critical: string[] };
  ai:             { warning: string[]; critical: string[] };
  safety?:        { warning: string[]; critical: string[] };
}

// NOTE: the old `thresholds` subtree was removed — the controller never read
// it; real alert thresholds live in ControllerSettings.alertThresholds.
export interface NotificationConfigUI {
  channels: NotificationChannelConfig;
  routing: NotificationRouting;
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
    exceptCritical: boolean;
    exceptSensorStale: boolean;
  };
  rateLimit: { maxPerHour: number; maxPerDay: number };
  repeatIntervalMinutes: number;
}

export interface ControllerSettings {
  id: string;
  name: string;
  timezone: string;
  units: 'metric' | 'imperial';
  alertsEnabled: boolean;
  aiNotificationsEnabled?: boolean;
  irrigationInfoNotificationsEnabled?: boolean;
  /** When false (default), mute crop-steering / Irrigation AI yellow+info alerts. Critical always sends. */
  cropSteeringWarningNotificationsEnabled?: boolean;
  notificationCategories?: Partial<Record<string, boolean>>;
  notificationConfig?: NotificationConfigUI;
  thermalSafety?: {
    enabled: boolean;
    sensorStaleThresholdMin: number;
    highTempThresholdC: number;
    highTempClearThresholdC: number;
    safetyDimLevel: number;
    startupGraceMin: number;
  };
  alertThresholds?: AlertThresholdsConfig;
  protectionMode?: 'notification' | 'autonomous';
}

export interface HardwareChannel {
  id: string;
  type: 'relay' | 'analog_out' | 'analog_in' | 'digital_in';
  channel: number;
  slot: number;
  deviceId?: string;
  deviceName?: string;
  state?: boolean;
  value?: number;
}

// ============================================
// Additional Hooks
// ============================================

/**
 * Hook for system logs from Firestore
 * Controller syncs logs to Firestore, frontend reads from Firestore only
 * 
 * Performance optimizations:
 * - Reduced default limit from 500 to 100
 * - Time filtering moved to Firestore query when possible
 * - Dynamic limits based on time range
 */
export function useLogs(options?: { 
  limit?: number; 
  category?: string;
  severity?: string;
  roomId?: string;
  deviceId?: string;
  timeRange?: '1h' | '6h' | '24h' | '7d' | 'all';
  search?: string;
  correlationId?: string;
}) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Calculate time cutoff and optimal limit based on timeRange
  const getTimeConfig = useCallback(() => {
    const now = new Date();
    switch (options?.timeRange) {
      case '1h': return { cutoff: new Date(now.getTime() - 60 * 60 * 1000), limit: 50 };
      case '6h': return { cutoff: new Date(now.getTime() - 6 * 60 * 60 * 1000), limit: 100 };
      case '24h': return { cutoff: new Date(now.getTime() - 24 * 60 * 60 * 1000), limit: 200 };
      case '7d': return { cutoff: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), limit: 300 };
      default: return { cutoff: null, limit: 100 };
    }
  }, [options?.timeRange]);

  useEffect(() => {
    setLoading(true);
    
    const timeConfig = getTimeConfig();
    const queryLimit = options?.limit || timeConfig.limit;
    
    // Build query constraints
    const constraints = [
      where('controllerId', '==', CONTROLLER_ID),
    ];
    
    // Add time filter to query if available (reduces data transfer)
    if (timeConfig.cutoff) {
      constraints.push(where('timestamp', '>=', Timestamp.fromDate(timeConfig.cutoff)));
    }
    
    // Query Firestore for logs with optimized limit
    const q = query(
      collection(db, COLLECTIONS.logs),
      ...constraints,
      orderBy('timestamp', 'desc'),
      limit(queryLimit)
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        let logList: LogEntry[] = snapshot.docs.map(doc => {
          const data = doc.data();
          const details = data.details || {};
          
          // Extract IDs from top-level or details object
          const roomId = data.roomId || details.roomId;
          const deviceId = data.deviceId || details.deviceId;
          const ruleId = data.ruleId || details.ruleId;
          const sensorId = data.sensorId || details.sensorId;
          
          // Extract human-readable names (check both flat fields and nested names object)
          const names = data.names || details.names || {};
          const roomName = details.roomName || names.room;
          const deviceName = details.deviceName || details.device || names.device;
          const ruleName = details.ruleName || details.rule || details.name || names.rule;
          const sensorName = details.sensorName || details.sensor || names.sensor;
          
          // Build a meaningful message from available data
          const message = data.event || data.message || '';
          
          // Normalize severity: backend uses 'warning'/'critical', frontend expects 'warn'/'error'
          const rawLevel = data.level || data.severity || 'info';
          const level = rawLevel === 'warning' ? 'warn' : rawLevel === 'critical' ? 'error' : rawLevel;
          
          return {
            id: doc.id,
            timestamp: data.timestamp?.toDate?.() || (typeof data.timestamp === 'string' ? new Date(data.timestamp) : new Date()),
            level,
            category: data.category || details.category || 'system',
            message,
            details,
            correlationId: data.correlationId,
            // IDs for filtering
            roomId,
            deviceId,
            ruleId,
            sensorId,
            // Names for display
            roomName,
            deviceName,
            ruleName,
            sensorName,
            action: data.action || details.action || data.event,
            source: data.source || details.source,
          };
        });
        
        // Apply remaining client-side filters (these can't easily be done in Firestore)
        if (options?.roomId && options.roomId !== 'all') {
          logList = logList.filter(l => l.roomId === options.roomId);
        }
        if (options?.deviceId && options.deviceId !== 'all') {
          logList = logList.filter(l => l.deviceId === options.deviceId);
        }
        if (options?.category && options.category !== 'all') {
          logList = logList.filter(l => l.category === options.category);
        }
        if (options?.severity && options.severity !== 'all') {
          logList = logList.filter(l => l.level === options.severity);
        }
        if (options?.search) {
          const searchLower = options.search.toLowerCase();
          logList = logList.filter(l => 
            l.message.toLowerCase().includes(searchLower) ||
            l.category.toLowerCase().includes(searchLower) ||
            (l.action && l.action.toLowerCase().includes(searchLower)) ||
            (l.deviceName && l.deviceName.toLowerCase().includes(searchLower)) ||
            (l.roomName && l.roomName.toLowerCase().includes(searchLower)) ||
            (l.ruleName && l.ruleName.toLowerCase().includes(searchLower)) ||
            (l.details && JSON.stringify(l.details).toLowerCase().includes(searchLower))
          );
        }
        if (options?.correlationId) {
          logList = logList.filter(l => l.correlationId === options.correlationId);
        }
        
        setLogs(logList);
        setLoading(false);
      },
      (err) => {
        // Handle missing composite index error gracefully
        if (err.message?.includes('index')) {
          console.warn('Logs query requires composite index, falling back to simpler query');
          // Fall back to simpler query without orderBy
          const fallbackQuery = query(
            collection(db, COLLECTIONS.logs),
            where('controllerId', '==', CONTROLLER_ID),
            limit(queryLimit)
          );
          
          const fallbackUnsubscribe = onSnapshot(fallbackQuery,
            (snapshot) => {
              let logList: LogEntry[] = snapshot.docs.map(doc => {
                const data = doc.data();
                const details = data.details || {};
                const names = data.names || details.names || {};
                const rawLevel = data.level || data.severity || 'info';
                return {
                  id: doc.id,
                  timestamp: data.timestamp?.toDate?.() || (typeof data.timestamp === 'string' ? new Date(data.timestamp) : new Date()),
                  level: rawLevel === 'warning' ? 'warn' : rawLevel === 'critical' ? 'error' : rawLevel,
                  category: data.category || details.category || 'system',
                  message: data.event || data.message || '',
                  details,
                  correlationId: data.correlationId,
                  roomId: data.roomId || details.roomId,
                  deviceId: data.deviceId || details.deviceId,
                  ruleId: data.ruleId || details.ruleId,
                  sensorId: data.sensorId || details.sensorId,
                  roomName: details.roomName || names.room,
                  deviceName: details.deviceName || details.device || names.device,
                  ruleName: details.ruleName || details.rule || details.name || names.rule,
                  sensorName: details.sensorName || details.sensor || names.sensor,
                  action: data.action || details.action || data.event,
                  source: data.source || details.source,
                };
              });
              
              // Sort client-side
              logList.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
              
              // Apply all client-side filters including time
              if (timeConfig.cutoff) {
                logList = logList.filter(l => l.timestamp >= timeConfig.cutoff!);
              }
              if (options?.roomId && options.roomId !== 'all') {
                logList = logList.filter(l => l.roomId === options.roomId);
              }
              if (options?.deviceId && options.deviceId !== 'all') {
                logList = logList.filter(l => l.deviceId === options.deviceId);
              }
              if (options?.category && options.category !== 'all') {
                logList = logList.filter(l => l.category === options.category);
              }
              if (options?.severity && options.severity !== 'all') {
                logList = logList.filter(l => l.level === options.severity);
              }
              if (options?.search) {
                const searchLower = options.search.toLowerCase();
                logList = logList.filter(l => 
                  l.message.toLowerCase().includes(searchLower) ||
                  l.category.toLowerCase().includes(searchLower) ||
                  (l.action && l.action.toLowerCase().includes(searchLower)) ||
                  (l.deviceName && l.deviceName.toLowerCase().includes(searchLower)) ||
                  (l.roomName && l.roomName.toLowerCase().includes(searchLower)) ||
                  (l.ruleName && l.ruleName.toLowerCase().includes(searchLower)) ||
                  (l.details && JSON.stringify(l.details).toLowerCase().includes(searchLower))
                );
              }
              if (options?.correlationId) {
                logList = logList.filter(l => l.correlationId === options.correlationId);
              }
              
              setLogs(logList);
              setLoading(false);
            },
            (fallbackErr) => {
              console.error('Firestore logs fallback error:', fallbackErr);
              setError(fallbackErr);
              setLoading(false);
            }
          );
          
          return fallbackUnsubscribe;
        }
        
        console.error('Firestore logs error:', err);
        setError(err);
        setLoading(false);
        return () => {};
      }
    );
    
    return () => unsubscribe();
  }, [options?.limit, options?.category, options?.severity, options?.roomId, options?.deviceId, options?.timeRange, options?.search, options?.correlationId, getTimeConfig]);

  return { logs, loading, error };
}

/**
 * Hook to fetch all events in a correlation chain directly from Firestore.
 * Unlike getCorrelationChainEvents() which only searches already-loaded logs,
 * this queries Firestore for the complete chain regardless of what's in memory.
 */
export function useCorrelationChain(correlationId: string | null) {
  const [events, setEvents] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!correlationId) {
      setEvents([]);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, COLLECTIONS.logs),
      where('correlationId', '==', correlationId),
      orderBy('timestamp', 'asc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const chainEvents: LogEntry[] = snapshot.docs.map(doc => {
          const data = doc.data();
          const details = data.details || {};
          const names = data.names || details.names || {};
          const rawLevel = data.level || data.severity || 'info';
          return {
            id: doc.id,
            timestamp: data.timestamp?.toDate?.() || (typeof data.timestamp === 'string' ? new Date(data.timestamp) : new Date()),
            level: rawLevel === 'warning' ? 'warn' : rawLevel === 'critical' ? 'error' : rawLevel,
            category: data.category || details.category || 'system',
            message: data.event || data.message || '',
            details,
            correlationId: data.correlationId,
            roomId: data.roomId || details.roomId,
            deviceId: data.deviceId || details.deviceId,
            ruleId: data.ruleId || details.ruleId,
            sensorId: data.sensorId || details.sensorId,
            roomName: details.roomName || names.room,
            deviceName: details.deviceName || details.device || names.device,
            ruleName: details.ruleName || details.rule || details.name || names.rule,
            sensorName: details.sensorName || details.sensor || names.sensor,
            action: data.action || details.action || data.event,
            source: data.source || details.source,
          };
        });
        setEvents(chainEvents);
        setLoading(false);
      },
      (err) => {
        console.warn('useCorrelationChain query failed:', err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [correlationId]);

  return { events, loading };
}

/**
 * State change interface for device history
 */
export interface StateChange {
  timestamp: Date;
  state: boolean | number;
  duration?: number;
  trigger?: {
    type: 'manual' | 'rule' | 'schedule' | 'ai';
    name: string;
    userId?: string;
  };
}

/**
 * Device statistics computed from history
 */
export interface DeviceStats {
  totalRuntime: number;
  cycleCount: number;
  avgCycleDuration: number;
  maxCycleDuration: number;
  lastStateChange?: Date;
  efficiencyScore?: number;
}

/**
 * Hook for device history - fetches and transforms device state change logs
 */
export function useDeviceHistory(
  deviceId: string | null, 
  timeRange: '1h' | '6h' | '24h' | '7d' | '30d' = '24h'
) {
  const [history, setHistory] = useState<StateChange[]>([]);
  const [stats, setStats] = useState<DeviceStats>({
    totalRuntime: 0,
    cycleCount: 0,
    avgCycleDuration: 0,
    maxCycleDuration: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!deviceId) {
      setHistory([]);
      setLoading(false);
      return;
    }

    // Calculate time cutoff based on range
    const now = Date.now();
    const rangeMs: Record<string, number> = {
      '1h': 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
    };
    const cutoffTime = new Date(now - rangeMs[timeRange]);

    // Query logs for this device
    const q = query(
      collection(db, COLLECTIONS.logs),
      where('controllerId', '==', CONTROLLER_ID),
      where('details.deviceId', '==', deviceId),
      limit(500)
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const stateChanges: StateChange[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const timestamp = data.timestamp?.toDate?.() || new Date();
          
          // Filter by time range
          if (timestamp < cutoffTime) return;
          
          // Only include state change events
          if (data.category !== 'device' && data.category !== 'state') return;
          
          const details = data.details || {};
          const state = details.state ?? details.level ?? false;
          
          // Determine trigger type
          let trigger: StateChange['trigger'] | undefined;
          if (details.source || details.triggeredBy || data.triggeredBy) {
            const source = details.source || details.triggeredBy || data.triggeredBy;
            if (source === 'manual' || source === 'user') {
              trigger = { type: 'manual', name: details.userName || 'Manual', userId: details.userId };
            } else if (source === 'rule' || source === 'automation') {
              trigger = { type: 'rule', name: details.ruleName || 'Rule' };
            } else if (source === 'schedule' || source === 'scheduler') {
              trigger = { type: 'schedule', name: details.scheduleName || 'Schedule' };
            } else if (source === 'ai') {
              trigger = { type: 'ai', name: details.aiReason || 'AI Control' };
            }
          }
          
          stateChanges.push({
            timestamp,
            state,
            duration: details.duration,
            trigger,
          });
        });
        
        // Sort by timestamp descending (newest first)
        stateChanges.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        // Compute stats
        const computedStats = computeDeviceStats(stateChanges);
        
        setHistory(stateChanges);
        setStats(computedStats);
        setLoading(false);
      },
      (err) => {
        console.error('Device history error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [deviceId, timeRange]);

  return { history, stats, loading, error };
}

/**
 * Compute device statistics from state changes
 */
function computeDeviceStats(history: StateChange[]): DeviceStats {
  if (history.length === 0) {
    return {
      totalRuntime: 0,
      cycleCount: 0,
      avgCycleDuration: 0,
      maxCycleDuration: 0,
    };
  }

  let totalRuntime = 0;
  let cycleCount = 0;
  let maxCycleDuration = 0;
  const cycleDurations: number[] = [];

  // Process history in chronological order (oldest first)
  const chronological = [...history].reverse();
  let lastOnTime: Date | null = null;

  for (const change of chronological) {
    const isOn = typeof change.state === 'boolean' ? change.state : (change.state as number) > 0;
    
    if (isOn && !lastOnTime) {
      // Device turned ON
      lastOnTime = change.timestamp;
      cycleCount++;
    } else if (!isOn && lastOnTime) {
      // Device turned OFF - calculate duration
      const duration = change.timestamp.getTime() - lastOnTime.getTime();
      totalRuntime += duration;
      cycleDurations.push(duration);
      maxCycleDuration = Math.max(maxCycleDuration, duration);
      lastOnTime = null;
    }
  }

  // If device is still ON, count time until now
  if (lastOnTime) {
    const duration = Date.now() - lastOnTime.getTime();
    totalRuntime += duration;
    cycleDurations.push(duration);
    maxCycleDuration = Math.max(maxCycleDuration, duration);
  }

  const avgCycleDuration = cycleDurations.length > 0 
    ? cycleDurations.reduce((a, b) => a + b, 0) / cycleDurations.length 
    : 0;

  // Calculate efficiency score (rough estimate based on cycle regularity)
  let efficiencyScore: number | undefined;
  if (cycleDurations.length >= 3) {
    const variance = cycleDurations.reduce((sum, d) => sum + Math.pow(d - avgCycleDuration, 2), 0) / cycleDurations.length;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = avgCycleDuration > 0 ? (stdDev / avgCycleDuration) * 100 : 0;
    // Lower CV = more consistent = higher efficiency
    efficiencyScore = Math.max(0, Math.min(100, Math.round(100 - coefficientOfVariation)));
  }

  return {
    totalRuntime,
    cycleCount,
    avgCycleDuration,
    maxCycleDuration,
    lastStateChange: history[0]?.timestamp,
    efficiencyScore,
  };
}

/**
 * Hook for batches
 */
export function useBatches(roomId?: string) {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Bounded: batches accumulate over time; the UI never needs unbounded history.
    let q = query(
      collection(db, COLLECTIONS.batches),
      where('controllerId', '==', CONTROLLER_ID),
      limit(200)
    );

    if (roomId) {
      q = query(
        collection(db, COLLECTIONS.batches),
        where('controllerId', '==', CONTROLLER_ID),
        where('roomId', '==', roomId),
        limit(200)
      );
    }

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const batchList: Batch[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || `Batch ${doc.id}`,
            strain: data.strain || 'Unknown',
            roomId: data.roomId || '',
            phase: data.phase || 'vegetative',
            startDate: data.startDate?.toDate?.() || new Date(),
            expectedHarvest: data.expectedHarvest?.toDate?.(),
            status: data.status || 'active',
          };
        });
        setBatches(batchList);
        setLoading(false);
      },
      (err) => {
        console.error('Batches error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [roomId]);

  const addBatch = useCallback(async (batch: Omit<Batch, 'id'>) => {
    await addDoc(collection(db, COLLECTIONS.batches), {
      ...batch,
      controllerId: CONTROLLER_ID,
      createdAt: serverTimestamp(),
    });
  }, []);

  const updateBatch = useCallback(async (batchId: string, updates: Partial<Batch>) => {
    const batchRef = doc(db, COLLECTIONS.batches, batchId);
    await updateDoc(batchRef, { ...updates, updatedAt: serverTimestamp() });
  }, []);

  return { batches, loading, error, addBatch, updateBatch };
}

/** Recursively strip `undefined` values from an object before Firestore writes.
 *  Firestore rejects `undefined`; this preserves `null` (used for disabled thresholds),
 *  arrays, Timestamps, FieldValue sentinels, and other non-plain objects. */
function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.getPrototypeOf(value) === Object.prototype
    ) {
      result[key] = stripUndefined(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Hook for controller settings
 */
export function useSettings() {
  const [settings, setSettings] = useState<ControllerSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const settingsRef = doc(db, 'ai_controller_settings', CONTROLLER_ID);
    
    const unsubscribe = onSnapshot(settingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setSettings({
            id: snapshot.id,
            name: data.name || 'AI Controller',
            timezone: data.timezone || 'Europe/Prague',
            units: data.units || 'metric',
            alertsEnabled: data.alertsEnabled ?? true,
            aiNotificationsEnabled: data.aiNotificationsEnabled ?? true,
            irrigationInfoNotificationsEnabled: data.irrigationInfoNotificationsEnabled ?? true,
            cropSteeringWarningNotificationsEnabled: data.cropSteeringWarningNotificationsEnabled ?? false,
            notificationCategories: data.notificationCategories ?? {},
            notificationConfig: data.notificationConfig || undefined,
            thermalSafety: data.thermalSafety || undefined,
            alertThresholds: data.alertThresholds || undefined,
            protectionMode: data.protectionMode || undefined,
          });
        } else {
          // Create default settings if none exist
          setSettings({
            id: CONTROLLER_ID,
            name: 'AI Controller',
            timezone: 'Europe/Prague',
            units: 'metric',
            alertsEnabled: true,
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Settings error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const saveSettings = useCallback(async (updates: Partial<ControllerSettings>) => {
    const settingsRef = doc(db, 'ai_controller_settings', CONTROLLER_ID);
    const clean = stripUndefined({ ...updates, updatedAt: serverTimestamp() });
    await updateDoc(settingsRef, clean).catch(async () => {
      const { setDoc } = await import('firebase/firestore');
      await setDoc(settingsRef, clean, { merge: true });
    });
  }, []);

  return { settings, loading, error, saveSettings };
}

/**
 * Hook for hardware channels (for I/O mapping)
 */
export function useChannels() {
  const { devices } = useDevices();
  const [channels, setChannels] = useState<HardwareChannel[]>([]);

  useEffect(() => {
    // Build channel list based on WAGO 750-363 configuration
    // Slots 1-6: 6x 750-559 4-Channel Analog Out = 24 analog channels
    // Slots 7-18: 12x 750-515 4-Channel Relay = 48 relay channels
    const analogChannels: HardwareChannel[] = Array.from({ length: 24 }, (_, i) => {
      const ch = i + 1;
      const assignedDevice = devices.find(d => d.channel === ch && d.type === 'light');
      return {
        id: `analog-${ch}`,
        type: 'analog_out' as const,
        channel: ch,
        slot: 1,
        deviceId: assignedDevice?.id,
        deviceName: assignedDevice?.name,
        value: assignedDevice?.level,
      };
    });

    const relayChannels: HardwareChannel[] = Array.from({ length: 48 }, (_, i) => {
      const ch = i + 1;
      const assignedDevice = devices.find(d => d.channel === ch && d.type !== 'light');
      return {
        id: `relay-${ch}`,
        type: 'relay' as const,
        channel: ch,
        slot: 2,
        deviceId: assignedDevice?.id,
        deviceName: assignedDevice?.name,
        state: assignedDevice?.state,
      };
    });

    setChannels([...analogChannels, ...relayChannels]);
  }, [devices]);

  const assignChannel = useCallback(async (channelId: string, deviceId: string) => {
    const deviceRef = doc(db, 'controllers', CONTROLLER_ID, 'devices', deviceId);
    const [, channelNum] = channelId.split('-');
    await updateDoc(deviceRef, { 
      channel: parseInt(channelNum),
      updatedAt: serverTimestamp() 
    });
  }, []);

  const unassignChannel = useCallback(async (deviceId: string) => {
    const deviceRef = doc(db, 'controllers', CONTROLLER_ID, 'devices', deviceId);
    await updateDoc(deviceRef, { 
      channel: 0,
      updatedAt: serverTimestamp() 
    });
  }, []);

  return { channels, assignChannel, unassignChannel };
}

/**
 * Hook for system metrics - reads from controller's Firestore heartbeat data
 */
export function useSystemMetrics(_pollInterval: number = 5000) {
  // Reads the same shared controller heartbeat doc as useControllerStatus —
  // no second listener on controllers/{CONTROLLER_ID}.
  const shared = useSharedSnapshot<Record<string, unknown> | null>(
    CONTROLLER_DOC_KEY,
    startControllerDocListener,
  );

  const metrics = useMemo(() => {
    const sm = (shared.value as Record<string, any> | null | undefined)?.systemMetrics;
    if (!sm) return null;
    return {
      cpu: sm.cpu ?? 0,
      memory: sm.memoryMB ?? 0,
      uptime: sm.uptimeHours ?? 0,
    };
  }, [shared.value]);

  return { metrics, loading: !shared.loaded, error: shared.error };
}

// ============================================
// Sensor summary (dashboard status bar)
// ============================================

export interface SensorSummaryItem {
  id: string;
  name: string;
  roomName: string;
  status: 'warning' | 'offline';
}

export interface SensorSummary {
  total: number;
  onlineCount: number;
  degradedSensors: SensorSummaryItem[];
  loading: boolean;
}

function isClimateRegistryDoc(data: Record<string, unknown>): boolean {
  return (
    data.temperature != null ||
    data.humidity != null ||
    data.co2 != null ||
    data.vpd != null
  );
}

/**
 * Aggregates physical sensors from sensor_registry for the dashboard status bar.
 * One registry document = one physical sensor (Comet or SDI-12).
 */
export function useSensorSummary(rooms: Room[]): SensorSummary {
  // Shares the registry listener with useSoilSensors (see SENSOR_REGISTRY_KEY).
  const shared = useSharedSnapshot<RegistryDoc[]>(SENSOR_REGISTRY_KEY, startSensorRegistryListener);
  const registryDocs = shared.value ?? EMPTY_REGISTRY_DOCS;
  const loading = !shared.loaded;
  const [, setTick] = useState(0);

  // Recompute warning/offline from age even when Firestore is quiet
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const roomNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rooms) map.set(r.id, r.name);
    return map;
  }, [rooms]);

  return useMemo(() => {
    const physicalSensors: Array<{
      id: string;
      name: string;
      roomName: string;
      status: ReturnType<typeof getSensorStatus>;
    }> = [];

    for (const { id, data } of registryDocs) {
      const isSdi12 = data.type === 'sdi12';
      const isClimate = !isSdi12 && isClimateRegistryDoc(data);
      if (!isSdi12 && !isClimate) continue;

      const lastSeenRaw = data.lastSeen ?? data.updatedAt;
      let lastUpdated: Date | null = null;
      if (lastSeenRaw && typeof (lastSeenRaw as { toDate?: () => Date }).toDate === 'function') {
        lastUpdated = (lastSeenRaw as { toDate: () => Date }).toDate();
      } else if (lastSeenRaw instanceof Date) {
        lastUpdated = lastSeenRaw;
      }

      const roomId = String(data.roomId || data.room || '');
      const roomName = roomNameById.get(roomId) || (roomId ? roomId : 'Unassigned');

      let name = typeof data.name === 'string' ? data.name : '';
      if (!name) {
        if (isSdi12) {
          const addr = data.sensorAddress || id.replace(/^sdi12-/, '');
          name = `SDI-12 @ ${addr}`;
        } else {
          const ipSuffix = id.replace(/^comet-/, '').split('-').pop() || '???';
          name = `Comet .${ipSuffix}`;
        }
      }

      physicalSensors.push({
        id,
        name,
        roomName,
        status: getSensorStatus(lastUpdated),
      });
    }

    const onlineCount = physicalSensors.filter(s => s.status === 'online').length;
    const degradedSensors: SensorSummaryItem[] = physicalSensors
      .filter(s => s.status !== 'online')
      .map(s => ({
        id: s.id,
        name: s.name,
        roomName: s.roomName,
        status: s.status as 'warning' | 'offline',
      }));

    return {
      total: physicalSensors.length,
      onlineCount,
      degradedSensors,
      loading,
    };
  }, [registryDocs, roomNameById, loading]);
}

